import {useNavigate} from 'react-router-dom';
import {Box, Chip, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import Loading from '../../common/Loading';
import styles from './styles/faq.module.css';

const LEARNING_STATUS_META = {
    PROGRESS: { label: '학습 중', color: 'warning' },
    COMPLETED: { label: '학습 완료', color: 'success' },
    FAILURE: { label: '학습 실패', color: 'error' }
};

function LearningStatusChip({ status }) {
    const meta = status ? LEARNING_STATUS_META[status] : null;
    if (!meta) {
        return (
            <Chip
                label='-'
                size='small'
                variant='outlined'
                sx={{ color: '#8a8a8a', borderColor: '#e0e0e0', fontWeight: 500 }}
            />
        );
    }
    return (
        <Chip
            label={meta.label}
            size='small'
            color={meta.color}
            variant='outlined'
            sx={{ fontWeight: 500 }}
        />
    );
}

const pad2 = (n) => String(n).padStart(2, '0');

const formatDate = (value) => {
    if (!value) return '-';
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value).slice(0, 16);
        return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    } catch {
        return String(value).slice(0, 16);
    }
};

const resolveCategoryLabel = (item) => {
    const mainName = item.category_main_name ?? item.categoryMainName;
    const subName = item.category_sub_name ?? item.categorySubName;
    if (mainName && subName) return `${mainName} > ${subName}`;
    if (mainName) return mainName;
    return '-';
};

const ellipsisSx = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 0
};

function EllipsisCell({ text, ...props }) {
    return (
        <TableCell sx={ellipsisSx} {...props}>
            {text}
        </TableCell>
    );
}

export default function FaqTable({ listResponse, listLoading, listErrorResponse, onDeleteRequest, onRefreshList }) {
    const navigate = useNavigate();

    const handleRowClick = (id) => {
        navigate(`/admin/faq/${id}`);
    };

    const handleDeleteClick = (e, item) => {
        e.stopPropagation();
        if (onDeleteRequest) onDeleteRequest(item);
    };

    const handleRefreshClick = () => {
        if (onRefreshList) onRefreshList();
    };

    if (listLoading) {
        return (
            <Box className={styles.empty}>
                <Loading />
            </Box>
        );
    }

    if (listErrorResponse) {
        return (
            <Box className={styles.empty}>
                목록을 불러오는 중 오류가 발생했습니다.
            </Box>
        );
    }

    const items = Array.isArray(listResponse) ? listResponse : [];

    if (items.length === 0) {
        return (
            <Box className={styles.empty}>
                등록된 FAQ가 없습니다. 우측 상단의 <b>신규 등록</b> 버튼을 눌러 추가하세요.
            </Box>
        );
    }

    return (
        <Box className={styles.tableContainer}>
            <Table sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: '30%' }}>질문</TableCell>
                        <TableCell sx={{ width: '18%' }}>카테고리</TableCell>
                        <TableCell sx={{ width: '12%' }}>최신 작성자</TableCell>
                        <TableCell sx={{ width: '16%' }}>최신 작성일</TableCell>
                        <TableCell sx={{ width: '8%' }}>노출 상태</TableCell>
                        <TableCell sx={{ width: '10%' }}>
                            <Stack direction='row' spacing={0.5} alignItems='center'>
                                <span>학습 상태</span>
                                <Tooltip title='최신 학습 상태 확인'>
                                    <IconButton
                                        size='small'
                                        aria-label='최신 학습 상태 확인'
                                        onClick={handleRefreshClick}
                                        sx={{ p: 0.25 }}
                                    >
                                        <RefreshIcon fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </TableCell>
                        <TableCell sx={{ width: '6%' }} align='right'></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item) => {
                        const latestAt = item.updated_at || item.updatedAt || item.created_at || item.createdAt;
                        const latestBy = item.updated_by || item.updatedBy || item.created_by || item.createdBy || '-';
                        const isActive = item.is_active ?? item.isActive ?? true;
                        const learningStatus = item.status ?? null;
                        const isLearning = learningStatus === 'PROGRESS';
                        return (
                            <TableRow
                                key={item.id}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleRowClick(item.id)}
                            >
                                <EllipsisCell text={item.question} />
                                <EllipsisCell text={resolveCategoryLabel(item)} />
                                <EllipsisCell text={latestBy} />
                                <EllipsisCell text={formatDate(latestAt)} />
                                <TableCell>
                                    <Box
                                        component='span'
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 500,
                                            color: isActive ? 'primary.main' : 'secondary.main'
                                        }}
                                    >
                                        {isActive ? '활성화' : '비활성화'}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <LearningStatusChip status={learningStatus} />
                                </TableCell>
                                <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                                    <Tooltip title={isLearning ? '' : '삭제'}>
                                        <span>
                                            <IconButton
                                                size='small'
                                                aria-label='삭제'
                                                disabled={isLearning}
                                                onClick={(e) => handleDeleteClick(e, item)}
                                            >
                                                <DeleteOutlineIcon fontSize='small' />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
}
